import { useAppSelector } from 'app/hooks';
import { useUser } from 'hooks/useUser';
import { isCanvaSession } from 'utils/canvaSession';
import { isBuyer } from 'utils/rbac';

/**
 * Whether this session gets the SSO-buyer shop experience, as opposed to the ordinary one.
 *
 * Two kinds of SSO buyer must not get it, and both would otherwise be handed an empty shop:
 *
 * <ul>
 *   <li>A buyer arriving from a Canva publish. That experience is built around a label shape
 *   carrying `galleryType`, `companyLabelId` and `galleryLabelId`, which only the SSO-buyer
 *   label endpoint returns — a Canva visit reads the shop's own labels instead, so every
 *   branch keyed on those fields falls through and a category card ends up doing nothing at
 *   all when it is clicked.
 *   <li>A buyer whose org never had an SSO-buyer gallery configured. That catalogue is curated
 *   per org and most have none, so the endpoint answers with an empty list and the Shop menu
 *   and gallery page both render blank while the org's ordinary gallery is full of products.
 *   See `galleryModel.getGalleryLabels`, which records the answer.
 * </ul>
 *
 * Keyed on the session rather than the URL for the Canva case: the parked design id is only on
 * the gallery link, so a URL-based rule flips back the moment the buyer opens a product.
 *
 * So the rule lives here rather than being spelled out at each branch: one definition, and a
 * buyer who cannot use the SSO-buyer shop shops the catalogue the same way anyone else does.
 */
export const useSsoBuyerExperience = (): boolean => {
  const user = useUser();
  // Undefined until the labels have been read once. Treated as "yes" so the experience does not
  // flicker through the ordinary one on the way in; only a confirmed empty gallery turns it off.
  const ssoBuyerGallery = useAppSelector((state) => state.gallery.ssoBuyerGallery);

  return (
    isBuyer(user) &&
    Boolean(user?.isSsoUser) &&
    !isCanvaSession() &&
    ssoBuyerGallery !== false
  );
};
